/**
 * analytics/posthog.ts — thin wrapper around the PostHog snippet.
 *
 * Pages import `captureEvent`, `identifyUser`, and `resetUser` from here.
 * If the PostHog snippet hasn't loaded (e.g. blocked by an ad blocker, or
 * during SSR/tests), these are silent no-ops — they never throw.
 *
 * The snippet itself is loaded in `index.html` or via the PostHog npm init
 * call in your app entry point; this file does not bootstrap it.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

interface PostHog {
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
}

function getPosthog(): PostHog | null {
  if (typeof window === 'undefined') return null;
  const ph = (window as any).posthog;
  if (ph && typeof ph.capture === 'function') return ph as PostHog;
  return null;
}

/** Fire a PostHog event. Silently no-ops if PostHog isn't present. */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  try {
    getPosthog()?.capture(event, properties);
  } catch {
    // never throw from analytics
  }
}

/** Associate the current session with a user ID + optional traits. */
export function identifyUser(distinctId: string, properties?: Record<string, unknown>): void {
  try {
    getPosthog()?.identify(distinctId, properties);
  } catch {
    // swallow
  }
}

/** Forget the current user (call on logout). */
export function resetUser(): void {
  try {
    getPosthog()?.reset();
  } catch {
    // swallow
  }
}
